import { itemVolume } from '@/utilities/calculateVolume';
import { formatHHMM, parseHHMM } from '@/utilities/hhmm';
import { ITEM_TYPES, ASSEMBLY_TYPES } from '@/components/ShipmentForm/constants/freightTypes';

// --- display formatters -----------------------------------------------------

const dashIfNull = (v) => (v == null ? '--' : v);

const normalizeEnumKey = (value) =>
    (value ?? '')
        .toString()
        .toLowerCase()
        .replace(/\s/g, '');

const buildEnumLookup = (entries) => {
    const map = new Map();
    entries.forEach(({ value, label }) => {
        map.set(normalizeEnumKey(value), value);
        map.set(normalizeEnumKey(label), value);
    });
    return map;
};

// A SKU is not tied to a freight type, so every item type is valid for one.
const ITEM_TYPE_LOOKUP = buildEnumLookup(
    Object.values(ITEM_TYPES)
        .flat()
        .map((t) => ({ value: t.value, label: t.label }))
);
const ASSEMBLY_TYPE_LOOKUP = buildEnumLookup(ASSEMBLY_TYPES.map((t) => ({ value: t.value, label: t.display })));

const toEnumOrNull = (value, lookup) => lookup.get(normalizeEnumKey(value)) ?? null;

const toRateClassOrNull = (value) => (value ?? '').toString().trim().toUpperCase() || null;

export const formatServiceTime = (row) => {
    const { service_time } = row || {};
    if (service_time == null) return '--';
    return formatHHMM(service_time);
};

export const formatWeight = (row) => {
    const { weight } = row || {};
    if (weight == null) return '--';
    return `${weight} lbs`;
};

export const formatDimensions = (row) => {
    const { length, width, height } = row || {};
    if (length == null && width == null && height == null) return '--';
    return `${dashIfNull(length)}L ${dashIfNull(width)}W ${dashIfNull(height)}H`;
};

export const formatInventoryRange = (row) => {
    const { inventory_min, inventory_max } = row || {};
    if (inventory_min == null && inventory_max == null) return '--';
    return `${dashIfNull(inventory_min)} / ${dashIfNull(inventory_max)}`;
};

// --- parsing / coercion -----------------------------------------------------

export const toNumberOrNull = (value) => {
    if (value == null || value === '') return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
};

export const toIntOrNull = (value) => {
    const num = toNumberOrNull(value);
    return num == null ? null : Math.round(num);
};

export const isInventoryRangeInvalid = (minValue, maxValue) => {
    const min = toIntOrNull(minValue);
    const max = toIntOrNull(maxValue);
    if (min == null || max == null) return false;
    return min >= max;
};

export const buildSkuValues = (form = {}) => {
    const length = toNumberOrNull(form.length);
    const width = toNumberOrNull(form.width);
    const height = toNumberOrNull(form.height);
    return {
        sku: (form.sku || '').trim(),
        description: (form.description || '').trim() || null,
        service_time: parseHHMM(form.service_time),
        item_type_details: toEnumOrNull(form.item_type_details, ITEM_TYPE_LOOKUP),
        assembly_type: toEnumOrNull(form.assembly_type, ASSEMBLY_TYPE_LOOKUP),
        rate_class: toRateClassOrNull(form.rate_class),
        weight: toNumberOrNull(form.weight),
        length,
        width,
        height,
        cubes: computeCubes({ length, width, height }),
        inventory_min: toIntOrNull(form.inventory_min),
        inventory_max: toIntOrNull(form.inventory_max),
        serialized: form.serialized ?? true,
        sellable: form.sellable ?? true,
    };
};

// Volume in cubic feet, matching how order items compute cubes (itemVolume).
export const computeCubes = ({ length, width, height } = {}) => {
    const l = toNumberOrNull(length);
    const w = toNumberOrNull(width);
    const h = toNumberOrNull(height);
    if (l == null || w == null || h == null) return null;
    return itemVolume({ length: l, width: w, height: h });
};

// --- CSV import -------------------------------------------------------------

export const SKU_CSV_COLUMNS = [
    'SKU',
    'Description',
    'Item Type',
    'Assembly Type',
    'Rate Class',
    'Service Time',
    'Weight',
    'Length',
    'Width',
    'Height',
    'Inventory Min',
    'Inventory Max',
];

export const buildSkuCsvTemplate = () => `${SKU_CSV_COLUMNS.join(',')}\n`;

// Case-insensitive header lookup against a papaparse (header:true) row object.
const pick = (row, header) => {
    const key = Object.keys(row || {}).find((k) => k.trim().toLowerCase() === header.toLowerCase());
    return key != null ? row[key] : undefined;
};

// Map one CSV row to an item_dimensions insert object. Returns null for rows
// without a SKU (treated as blank/skippable).
export const parseSkuCsvRow = (row) => {
    if (!row) return null;
    const sku = (pick(row, 'SKU') ?? '').toString().trim();
    if (!sku) return null;

    const length = toNumberOrNull(pick(row, 'Length'));
    const width = toNumberOrNull(pick(row, 'Width'));
    const height = toNumberOrNull(pick(row, 'Height'));

    return {
        sku,
        description: (pick(row, 'Description') ?? '').toString().trim() || null,
        service_time: parseHHMM(pick(row, 'Service Time')),
        item_type_details: toEnumOrNull(pick(row, 'Item Type'), ITEM_TYPE_LOOKUP),
        assembly_type: toEnumOrNull(pick(row, 'Assembly Type'), ASSEMBLY_TYPE_LOOKUP),
        rate_class: toRateClassOrNull(pick(row, 'Rate Class')),
        weight: toNumberOrNull(pick(row, 'Weight')),
        length,
        width,
        height,
        cubes: computeCubes({ length, width, height }),
        inventory_min: toIntOrNull(pick(row, 'Inventory Min')),
        inventory_max: toIntOrNull(pick(row, 'Inventory Max')),
    };
};

export const parseSkuCsvRows = (rows) =>
    (rows || [])
        .map(parseSkuCsvRow)
        .filter(Boolean)
        .filter((row) => !isInventoryRangeInvalid(row.inventory_min, row.inventory_max));

const CSV_HEADER_TO_COLUMN = {
    description: 'description',
    'service time': 'service_time',
    weight: 'weight',
    length: 'length',
    width: 'width',
    height: 'height',
    'inventory min': 'inventory_min',
    'inventory max': 'inventory_max',
    'item type': 'item_type_details',
    'assembly type': 'assembly_type',
    'rate class': 'rate_class',
};

// Upsert update_columns limited to headers present in the uploaded CSV, so a
// partial sheet can't null out stored values on existing SKUs. cubes is derived,
// so it only updates when all three dimension columns come from the file.
export const getSkuUpdateColumns = (headers) => {
    const present = new Set((headers || []).map((h) => String(h).trim().toLowerCase()));
    const columns = Object.entries(CSV_HEADER_TO_COLUMN)
        .filter(([header]) => present.has(header))
        .map(([, column]) => column);
    if (['length', 'width', 'height'].every((c) => columns.includes(c))) {
        columns.push('cubes');
    }
    return columns;
};
